<template>
  <section class="statistics-card">
    <div class="card-header">
      <h3 class="card-title">
        版权数字化治理保护工程服务会员转化
      </h3>
      <div class="overall-rate">
        子渠道服务会员整体转化率：
        <strong>{{ summary.overallRate.toFixed(2) }}%</strong>
      </div>
    </div>

    <div class="conversion-grid">
      <article
        v-for="item in conversionItems"
        :key="item.code"
        class="conversion-item"
      >
        <div
          class="conversion-ring"
        >
          <svg
            class="conversion-ring-svg"
            viewBox="0 0 100 100"
            aria-hidden="true"
          >
            <circle
              class="conversion-ring-track"
              cx="50"
              cy="50"
              r="40"
            />
            <circle
              class="conversion-ring-value"
              cx="50"
              cy="50"
              r="40"
              pathLength="100"
              :stroke="item.color"
              :stroke-dasharray="`${item.rate} ${100 - item.rate}`"
            />
          </svg>
          <div class="conversion-ring-inner">
            <strong>{{ item.rate.toFixed(2) }}%</strong>
            <span>转化率</span>
          </div>
        </div>

        <div class="conversion-detail">
          <div class="detail-heading">
            <strong>{{ item.name }}</strong>
            <span>{{ item.total }} 个</span>
          </div>
          <div class="detail-row">
            <span>
              <i :style="{ backgroundColor: item.color }" />
              转化
            </span>
            <strong>{{ item.converted }} 个</strong>
          </div>
          <div class="detail-row">
            <span>
              <i class="is-muted" />
              未转化
            </span>
            <strong>{{ item.unconverted }} 个</strong>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup>
import { onMounted, shallowRef } from 'vue'
import { getServiceMemberConversionStatistics } from '@/api/primaryChannelStatistics'
import { ChannelIdentityTypeEnum } from '@/enums/ChannelTypeEnum'

const CONVERSION_DEFINITIONS = [
  {
    code: ChannelIdentityTypeEnum.CITY_ASSOC.code,
    name: '直属事业单位及市级文艺家协会服务会员总数',
    color: '#6B4FF7'
  },
  {
    code: ChannelIdentityTypeEnum.ART_NEW_ORG.code,
    name: '文艺两新组织服务会员总数',
    color: '#FFC51B'
  },
  {
    code: ChannelIdentityTypeEnum.DISTRICT_ASSOC.code,
    name: '区级文艺家协会服务会员总数',
    color: '#1478F2'
  },
  {
    code: ChannelIdentityTypeEnum.SUPPORT_REGION.code,
    name: '对口帮扶地区组织服务会员总数',
    color: '#16C68B'
  }
]

const summary = shallowRef(buildDefaultSummary())
const conversionItems = shallowRef(buildDefaultItems())

function buildDefaultItems() {
  return CONVERSION_DEFINITIONS.map(item => ({
    ...item,
    total: 0,
    converted: 0,
    unconverted: 0,
    rate: 0
  }))
}

function buildDefaultSummary() {
  return {
    total: 0,
    convertedTotal: 0,
    unconvertedTotal: 0,
    overallRate: 0
  }
}

function toNumber(value) {
  const number = Number(String(value ?? '').replace('%', ''))
  return Number.isFinite(number) ? number : 0
}

function normalizeStatistics(payload = {}) {
  const sourceItems = Array.isArray(payload.items) ? payload.items : []
  summary.value = {
    total: toNumber(payload.total),
    convertedTotal: toNumber(payload.convertedTotal),
    unconvertedTotal: toNumber(payload.unconvertedTotal),
    overallRate: Math.min(Math.max(toNumber(payload.overallConversionRate), 0), 100)
  }
  conversionItems.value = CONVERSION_DEFINITIONS.map((definition) => {
    const source = sourceItems.find(item => item.code === definition.code) || {}
    return {
      ...definition,
      total: toNumber(source.total),
      converted: toNumber(source.convertedTotal),
      unconverted: toNumber(source.unconvertedTotal),
      rate: Math.min(Math.max(toNumber(source.conversionRate), 0), 100)
    }
  })
}

async function loadStatistics() {
  try {
    const response = await getServiceMemberConversionStatistics()
    normalizeStatistics(response && response.code === 200 ? response.data : {})
  } catch (error) {
    console.error('查询一级渠道服务会员转化统计失败:', error)
    normalizeStatistics()
  }
}

onMounted(loadStatistics)
</script>

<style lang="scss" scoped>
.statistics-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 22px 24px 28px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.card-title {
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
}

.overall-rate {
  color: #333;
  font-size: 14px;
  white-space: nowrap;

  strong {
    margin-left: 8px;
    font-size: 16px;
  }
}

.conversion-grid {
  display: grid;
  margin-top: 32px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 48px;
  row-gap: 36px;
}

.conversion-item {
  display: grid;
  min-width: 0;
  grid-template-columns: 190px minmax(0, 1fr);
  align-items: center;
  gap: 24px;
}

.conversion-ring {
  position: relative;
  width: 168px;
  height: 168px;
  margin: 0 auto;
  border-radius: 50%;
}

.conversion-ring-svg {
  display: block;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.conversion-ring-track,
.conversion-ring-value {
  fill: none;
  stroke-width: 18;
}

.conversion-ring-track {
  stroke: #f0f2f7;
}

.conversion-ring-value {
  stroke-linecap: butt;
}

.conversion-ring-inner {
  position: absolute;
  inset: 31px;
  display: flex;
  border-radius: 50%;
  background: #fff;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  strong {
    color: #222;
    font-size: 21px;
    line-height: 1.2;
  }

  span {
    margin-top: 5px;
    color: #999;
    font-size: 12px;
  }
}

.conversion-detail {
  min-width: 0;
}

.detail-heading {
  display: grid;
  min-height: 54px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px;

  strong {
    color: #333;
    font-size: 14px;
    line-height: 1.45;
  }

  span {
    color: #333;
    font-size: 14px;
    white-space: nowrap;
  }
}

.detail-row {
  display: flex;
  padding-top: 12px;
  align-items: center;
  justify-content: space-between;
  color: #333;
  font-size: 13px;

  span {
    display: inline-flex;
    align-items: center;
  }

  i {
    width: 10px;
    height: 10px;
    margin-right: 9px;
    border-radius: 50%;

    &.is-muted {
      background: #f0f2f7;
    }
  }
}

@media (max-width: 1100px) {
  .conversion-grid {
    column-gap: 24px;
  }

  .conversion-item {
    grid-template-columns: 145px minmax(0, 1fr);
    gap: 12px;
  }

  .conversion-ring {
    width: 136px;
    height: 136px;
  }

  .conversion-ring-inner {
    inset: 25px;

    strong {
      font-size: 17px;
    }
  }
}

@media (max-width: 768px) {
  .statistics-card {
    padding: 16px;
  }

  .card-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .card-title {
    font-size: 16px;
  }

  .overall-rate {
    white-space: normal;
  }

  .conversion-grid {
    grid-template-columns: 1fr;
    row-gap: 28px;
  }

  .conversion-item {
    grid-template-columns: 140px minmax(0, 1fr);
  }
}

@media (max-width: 480px) {
  .statistics-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .conversion-item {
    grid-template-columns: 110px minmax(0, 1fr);
    gap: 12px;
  }

  .conversion-ring {
    width: 104px;
    height: 104px;
  }

  .conversion-ring-inner {
    inset: 19px;

    strong {
      font-size: 15px;
    }
  }

  .detail-heading {
    gap: 8px;

    strong,
    span {
      font-size: 12px;
    }
  }
}
</style>
